import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";

const FAQS = [
  {
    category: "About HostFolio",
    questions: [
      {
        q: "What is HostFolio?",
        a: "HostFolio is a property management dashboard built specifically for Indian Airbnb hosts. It syncs bookings from Airbnb, Booking.com, MakeMyTrip, Goibibo, VRBO and Agoda into one place, provides Smart Pricing, manages guest communications, generates GST-ready P&L reports and automates housekeeping tasks.",
      },
      {
        q: "Who is HostFolio built for?",
        a: "HostFolio is built for Airbnb hosts and short-term rental property owners in India who manage 1 to unlimited properties. It is especially useful for hosts listing on multiple Indian and international platforms like MakeMyTrip, Goibibo, Airbnb and Booking.com.",
      },
      {
        q: "How is HostFolio different from other property management tools?",
        a: "Most property management tools are priced for Western markets at $100–200 per month and are not built for India. HostFolio is built specifically for Indian hosts — INR currency, GST compliance, SMS via Indian numbers, UPI payments via Razorpay and AI pricing using live Indian market data. Plans start at ₹1,149/month, with the channel manager included.",
      },
      {
        q: "Where is HostFolio available?",
        a: "HostFolio is available for Airbnb hosts across India including Goa, Bangalore, Mumbai, Manali, Coorg, Udaipur, Shimla, Rishikesh and Kerala. It is optimised for the Indian market with INR pricing, GST compliance and Indian SMS delivery.",
      },
    ],
  },
  {
    category: "Platform & Sync",
    questions: [
      {
        q: "Which platforms does HostFolio sync with?",
        a: "HostFolio syncs bookings from Airbnb, Booking.com, VRBO, MakeMyTrip, Goibibo and Agoda via iCal integration. New bookings from any platform automatically appear in your HostFolio dashboard and inbox within 4 hours.",
      },
      {
        q: "Does it work with MakeMyTrip and Goibibo?",
        a: "Yes. HostFolio syncs bookings from MakeMyTrip and Goibibo via iCal, in addition to Airbnb, Booking.com, VRBO and Agoda. This makes it one of the few property management tools that supports Indian OTAs natively.",
      },
      {
        q: "How does two-way iCal sync work?",
        a: "HostFolio can export your calendar back to Airbnb and Booking.com so that when you block dates in HostFolio, those dates are automatically blocked on all connected platforms. This prevents double bookings across all platforms.",
      },
      {
        q: "How long does setup take?",
        a: "Under 2 minutes. Paste your Airbnb iCal link and your bookings start syncing automatically. No technical knowledge is required.",
      },
    ],
  },
  {
    category: "Features",
    questions: [
      {
        q: "How does Smart Pricing work?",
        a: "HostFolio's Smart Pricing analyses live competitor prices on Airbnb in your exact area, combined with your own booking history and current occupancy rate, to suggest the optimal nightly rate for tonight. Hosts typically see 15–25% more revenue in the first month.",
      },
      {
        q: "Does HostFolio support GST compliance for Airbnb income?",
        a: "Yes. HostFolio automatically calculates GST on every booking — 0% for under ₹1,000 per night, 12% for ₹1,000–7,499 and 18% for ₹7,500 and above. It splits every payment into CGST, SGST and IGST based on guest location and generates a PDF P&L report ready for your CA.",
      },
      {
        q: "How does the task board and housekeeping automation work?",
        a: "After every guest checkout HostFolio automatically creates a cleaning task and sends an SMS alert to your assigned housekeeping staff. You can assign tasks, set deadlines and track completion from the task board without sending manual WhatsApp messages.",
      },
      {
        q: "Does HostFolio send automated messages to guests?",
        a: "Yes. HostFolio sends automated email and SMS messages for booking confirmations, check-in reminders and checkout reminders. The Smart Inbox also uses AI to draft reply suggestions in warm, professional or brief tones across all platforms.",
      },
      {
        q: "What is the Property ROI Evaluator?",
        a: "The Property ROI Evaluator is an AI-powered calculator that helps you decide whether a property is worth buying, renting or leasing for short-term rental. Enter the purchase price or rent amount and it calculates expected monthly revenue, occupancy rate, expenses and net return on investment based on live market data.",
      },
      {
        q: "Can I manage multiple properties?",
        a: "Yes. The Starter plan supports 3 properties, Pro supports 10 and Agency supports unlimited properties. All properties are visible in a single unified dashboard, calendar and inbox.",
      },
    ],
  },
  {
    category: "Pricing & Billing",
    questions: [
      {
        q: "Is HostFolio free?",
        a: "Yes. HostFolio is completely free for the first month with no credit card required. After the free trial, plans start at ₹1,149 per month for 1 property. There are no hidden fees and you can cancel anytime.",
      },
      {
        q: "What are the paid plans?",
        a: "After the free 1-month trial: Basic at ₹1,149/month for 1 property, Starter at ₹3,449/month for 3 properties, Pro at ₹11,449/month for 10 properties, and Agency on custom pricing for unlimited properties. Every paid plan includes the channel manager.",
      },
      {
        q: "What payment methods are accepted?",
        a: "UPI, credit/debit cards, net banking and wallets — powered by Razorpay. All payments are in Indian Rupees (INR).",
      },
      {
        q: "Can I cancel anytime?",
        a: "Yes. Cancel from Settings. You keep full access until the end of your billing period. No questions asked.",
      },
    ],
  },
  {
    category: "Security & Privacy",
    questions: [
      {
        q: "Is my data secure?",
        a: "All data is encrypted in your own private database partition. HTTPS-only. We never share or sell your data.",
      },
      {
        q: "Is HostFolio suitable for first-time Airbnb hosts?",
        a: "Yes. HostFolio is designed to be set up in under 2 minutes — paste your Airbnb iCal link and your bookings start syncing automatically. No technical knowledge required. The free 1-month trial means you can start with zero risk.",
      },
    ],
  },
];

export default function FAQ() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  let globalIndex = 0;

  return (
    <div className="min-h-screen bg-white font-outfit text-slate-700">

      {/* Header */}
      <header className="border-b border-slate-100 px-6 py-4 flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur-sm z-10">
        <button onClick={() => navigate("/")} className="font-manrope text-xl font-bold">
          <span className="text-slate-900">Host</span><span className="text-amber-500">Folio</span>
        </button>
        <button
          onClick={() => navigate("/login")}
          className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold px-5 py-2 rounded-full transition-colors"
        >
          Get Started Free
        </button>
      </header>

      {/* Hero */}
      <section className="py-16 text-center px-6 bg-gradient-to-b from-amber-50 to-white">
        <p className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-3">Got questions?</p>
        <h1 className="font-manrope text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Frequently Asked Questions
        </h1>
        <p className="text-slate-500 text-base max-w-xl mx-auto">
          Everything you need to know about HostFolio. Can't find your answer?{" "}
          <a href="mailto:support@hostfolio.in" className="text-amber-600 font-semibold hover:underline">
            Email us
          </a>
          .
        </p>
      </section>

      {/* FAQ Categories */}
      <main className="max-w-2xl mx-auto px-6 pb-24 space-y-12">
        {FAQS.map((section) => (
          <div key={section.category}>
            <h2 className="font-manrope text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              {section.category}
            </h2>
            <div className="space-y-3">
              {section.questions.map(({ q, a }) => {
                const idx = globalIndex++;
                const isOpen = openIndex === idx;
                return (
                  <div
                    key={idx}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
                    style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 group"
                    >
                      <span className="font-semibold text-slate-800 text-sm group-hover:text-amber-600 transition-colors">
                        {q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-slate-400 text-xl leading-none shrink-0 font-light"
                      >
                        +
                      </motion.span>
                    </button>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3"
                      >
                        {a}
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-400 rounded-3xl p-10 text-center"
          style={{ boxShadow: "0 16px 48px rgba(251,191,36,0.3)" }}>
          <h2 className="font-manrope text-2xl font-bold text-white mb-2">Ready to get started?</h2>
          <p className="text-white/80 text-sm mb-6">Free for 1 month. No credit card. 2-minute setup.</p>
          <button
            onClick={() => navigate("/login")}
            className="bg-white text-amber-600 font-bold px-8 py-3 rounded-full inline-flex items-center gap-2 hover:scale-105 transition-transform text-sm"
          >
            Start Free Trial <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        <a href="/" className="hover:text-slate-600 transition">← Back to HostFolio</a>
        <span className="mx-3">·</span>
        <a href="mailto:support@hostfolio.in" className="hover:text-slate-600 transition">support@hostfolio.in</a>
        <span className="mx-3">·</span>
        <a href="/privacy" className="hover:text-slate-600 transition">Privacy</a>
        <span className="mx-3">·</span>
        <a href="/terms" className="hover:text-slate-600 transition">Terms</a>
      </footer>
    </div>
  );
}
